/**
 * 📊 HeroStatSheetPage — Character-sheet panel for the logged-in hero (Feb 2026)
 *
 * Surfaces the full canonical stat set locked in the Stat Glossary:
 *   HP · PATK · MATK · PDEF · MDEF · Block · Crit · Block Crit · Charisma · Luck
 * Attack Speed + Dodge shown as TBD chips (planned).
 *
 * Base stat and Guild Shard buffs are rendered side-by-side so the
 * player can see EXACTLY how much of a stat came from their class vs
 * the guild upgrade tree.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart, Sword, Sparkles as SparkIcon, Shield, ShieldCheck,
  Target, Users, ScrollText, Star, Zap, Wind, Info, Gem, ShoppingCart,
  Package, FlaskConical, Clock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ─── Stat metadata (matches Stat Glossary canon) ───────────
const STATS = [
  { key: "hp",         label: "Hit Points",     short: "HP",         Icon: Heart,       accent: "text-red-400",     ring: "ring-red-500/40",     unit: "" },
  { key: "patk",       label: "Physical Attack", short: "PATK",       Icon: Sword,       accent: "text-orange-400",  ring: "ring-orange-500/40",  unit: "" },
  { key: "matk",       label: "Magic Attack",    short: "MATK",       Icon: SparkIcon,   accent: "text-purple-400",  ring: "ring-purple-500/40",  unit: "" },
  { key: "pdef",       label: "Physical Defense",short: "PDEF",       Icon: Shield,      accent: "text-blue-400",    ring: "ring-blue-500/40",    unit: "" },
  { key: "mdef",       label: "Magic Defense",   short: "MDEF",       Icon: ShieldCheck, accent: "text-cyan-400",    ring: "ring-cyan-500/40",    unit: "" },
  { key: "block",      label: "Block Rate",      short: "Block",      Icon: Shield,      accent: "text-emerald-400", ring: "ring-emerald-500/40", unit: "%" },
  { key: "crit",       label: "Critical Strike", short: "Crit",       Icon: Target,      accent: "text-yellow-400",  ring: "ring-yellow-500/40",  unit: "%" },
  { key: "block_crit", label: "Block Critical",  short: "Block Crit", Icon: Target,      accent: "text-lime-400",    ring: "ring-lime-500/40",    unit: "%" },
  { key: "charisma",   label: "Charisma",        short: "Charisma",   Icon: Users,       accent: "text-fuchsia-400", ring: "ring-fuchsia-500/40", unit: "" },
  { key: "luck",       label: "Luck",            short: "Luck",       Icon: Star,        accent: "text-amber-400",   ring: "ring-amber-500/40",   unit: "" },
];

function fmt(v, unit) {
  if (v == null) return "—";
  if (unit === "%") {
    return `${Number(v).toFixed(Number(v) % 1 === 0 ? 0 : 1)}%`;
  }
  return new Intl.NumberFormat("en-US").format(Math.round(v));
}


// ─── Stat Card ─────────────────────────────────────────────
function StatCard({ meta, base, final, buff, previewDelta, crystal, equipContrib, apexContrib }) {
  const { Icon } = meta;
  const guildBuff  = Number(buff || 0);
  const gearBuff   = Number(equipContrib || 0);
  const apexBuff   = Number(apexContrib || 0);
  const anyBuff    = Math.abs(guildBuff) > 0.001 || Math.abs(gearBuff) > 0.001 || Math.abs(apexBuff) > 0.001;
  const previewUp   = Number(previewDelta || 0) > 0;
  const previewDown = Number(previewDelta || 0) < 0;
  const crystalLvl = crystal?.level || 0;
  const crystalPct = crystal?.boost_pct || 0;
  return (
    <div
      data-testid={`hero-stat-${meta.key}`}
      className={`rounded-xl border border-neutral-800 bg-black/50 p-4 relative overflow-hidden ring-1 ${anyBuff ? meta.ring : "ring-transparent"}`}
    >
      <div className="flex items-start justify-between mb-1">
        <Icon size={18} className={meta.accent} />
        <span className="text-[9px] uppercase tracking-widest text-neutral-500">{meta.short}</span>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{meta.label}</div>
      <div className={`text-3xl font-bold mt-1 font-mono ${meta.accent}`}>
        {fmt(final, meta.unit)}
      </div>
      <div className="mt-1 text-[10px] text-neutral-500 flex items-center gap-1 flex-wrap">
        <span>Base: <span className="font-mono">{fmt(base, meta.unit)}</span></span>
        {Math.abs(guildBuff) > 0.001 && (
          <span className="flex items-center gap-0.5">
            <span className="text-neutral-600">+</span>
            <span className={`font-mono ${meta.accent}`} data-testid={`hero-stat-${meta.key}-buff`}>{fmt(guildBuff, meta.unit)}</span>
            <span className="text-neutral-600 uppercase tracking-widest">guild</span>
          </span>
        )}
        {Math.abs(apexBuff) > 0.001 && (
          <span className="flex items-center gap-0.5">
            <span className="text-neutral-600">+</span>
            <span className={`font-mono ${meta.accent}`}>{fmt(apexBuff, meta.unit)}</span>
            <span className="text-neutral-600 uppercase tracking-widest">apex</span>
          </span>
        )}
        {Math.abs(gearBuff) > 0.001 && (
          <span className="flex items-center gap-0.5" data-testid={`hero-stat-${meta.key}-gear`}>
            <span className="text-neutral-600">+</span>
            <span className={`font-mono ${meta.accent}`}>{fmt(gearBuff, meta.unit)}</span>
            <span className="text-neutral-600 uppercase tracking-widest">gear</span>
          </span>
        )}
      </div>
      {crystalLvl > 0 && (
        <div
          data-testid={`hero-stat-${meta.key}-crystal`}
          className="mt-1 text-[10px] flex items-center gap-1 text-fuchsia-300"
        >
          <Gem size={10} />
          <span className="font-mono">Lv {crystalLvl}</span>
          <span className="text-fuchsia-400/70">+{crystalPct.toFixed(1)}%</span>
        </div>
      )}
      {(previewUp || previewDown) && (
        <div
          data-testid={`hero-stat-${meta.key}-preview-delta`}
          className={`absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${
            previewUp ? "bg-emerald-900/50 border border-emerald-500/50 text-emerald-300"
                      : "bg-red-900/50 border border-red-500/50 text-red-300"
          }`}
        >
          {previewUp ? "▲" : "▼"} {fmt(Math.abs(previewDelta), meta.unit)}
        </div>
      )}
    </div>
  );
}


// ─── TBD Stat Card (planned) ───────────────────────────────
function TBDCard({ label, short, Icon }) {
  return (
    <div className="rounded-xl border border-dashed border-neutral-700 bg-black/30 p-4 opacity-70" data-testid={`hero-stat-tbd-${short.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-1">
        <Icon size={18} className="text-neutral-500" />
        <span className="text-[9px] uppercase tracking-widest text-neutral-500">{short}</span>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-3xl font-bold mt-1 font-mono text-neutral-600">???</div>
      <div className="mt-1 text-[10px] text-neutral-500 italic">Planned</div>
    </div>
  );
}


// ─── Soul Crystal cost helper (mirrors backend) ────────────
const SC_SEED_COSTS = [10, 30, 50, 110];
const SC_MAX_LEVEL = 80;
function scCostForStep(fromLv) {
  if (fromLv < 0 || fromLv >= SC_MAX_LEVEL) return null;
  if (fromLv < SC_SEED_COSTS.length) return SC_SEED_COSTS[fromLv];
  const n = fromLv;
  return Math.round(40 * n + 20 * Math.pow(n - 4, 2));
}
function scQuoteLevels(currentLv, levels) {
  let total = 0, added = 0;
  const breakdown = [];
  for (let i = 0; i < levels; i++) {
    const step = scCostForStep(currentLv + i);
    if (step == null) break;
    total += step;
    breakdown.push({ from: currentLv + i, to: currentLv + i + 1, cost: step });
    added += 1;
  }
  return { total, added, breakdown };
}


// ─── Soul Crystal Socket Panel ─────────────────────────────
function SoulCrystalSocketRow({ socket, meta, inventory, pctPerLevel, onUpgrade, busyKey }) {
  const [levels, setLevels] = useState(1);
  const cur = socket.level;
  const atCap = socket.at_cap;
  // Cap slider to max achievable given inventory OR remaining cap
  const maxByCap = Math.max(1, SC_MAX_LEVEL - cur);
  const [lvls, sliderMax] = (() => {
    // Compute affordable by walking cost curve
    let running = 0, aff = 0;
    for (let i = 0; i < maxByCap; i++) {
      const s = scCostForStep(cur + i);
      if (s == null) break;
      if (running + s > inventory) break;
      running += s;
      aff += 1;
    }
    return [Math.min(levels, Math.max(1, aff)), Math.max(1, aff || 1)];
  })();
  const quote = scQuoteLevels(cur, lvls);
  const nextStep = scCostForStep(cur);
  const affordable = quote.total <= inventory && quote.added > 0 && !atCap;
  const busy = busyKey === socket.stat_key;

  return (
    <div
      data-testid={`soul-crystal-row-${socket.stat_key}`}
      className="rounded-lg border border-neutral-800 bg-black/40 p-3 hover:border-fuchsia-500/40 transition-colors"
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <meta.Icon size={16} className={meta.accent} />
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-200">
              {meta.short}
            </div>
            <div className="text-[9px] text-neutral-500">{meta.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Gem size={12} className="text-fuchsia-300" />
          <span className="font-mono text-fuchsia-300 text-sm" data-testid={`soul-crystal-lv-${socket.stat_key}`}>
            Lv {cur}/{SC_MAX_LEVEL}
          </span>
          <span className="text-fuchsia-400/70 text-[10px] font-mono">
            +{(cur * pctPerLevel).toFixed(1)}%
          </span>
        </div>
      </div>

      {atCap ? (
        <div className="text-[10px] text-fuchsia-400 italic text-center py-2">
          🌌 Socket at max power. +{(SC_MAX_LEVEL * pctPerLevel).toFixed(0)}%
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-1">
            <input
              type="range"
              min={1}
              max={sliderMax}
              value={lvls}
              onChange={(e) => setLevels(Math.max(1, Math.min(sliderMax, Number(e.target.value))))}
              data-testid={`soul-crystal-slider-${socket.stat_key}`}
              className="flex-1 accent-fuchsia-500"
              disabled={sliderMax <= 1 && !affordable}
            />
            <span className="text-[10px] font-mono text-fuchsia-300 w-16 text-right">
              +{lvls} lv
            </span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-neutral-500">
              Next step:{" "}
              <span className="font-mono text-neutral-300">{nextStep ?? "—"}</span>
              <span className="text-fuchsia-400/70"> = +{pctPerLevel}%</span>
            </span>
            <span className={`font-mono font-bold ${affordable ? "text-fuchsia-300" : "text-red-400"}`}>
              {quote.total.toLocaleString()} 💎
            </span>
          </div>
          <button
            onClick={() => onUpgrade(socket.stat_key, lvls)}
            disabled={!affordable || busy}
            data-testid={`soul-crystal-upgrade-${socket.stat_key}`}
            className={`mt-2 w-full text-[10px] uppercase tracking-widest font-bold py-1.5 rounded border transition-colors ${
              affordable && !busy
                ? "border-fuchsia-500/60 bg-fuchsia-900/30 text-fuchsia-200 hover:bg-fuchsia-900/50"
                : "border-neutral-800 bg-neutral-950/60 text-neutral-600 cursor-not-allowed"
            }`}
          >
            {busy
              ? "Socketing..."
              : `Upgrade +${lvls} → Lv ${cur + lvls} (+${((cur + lvls) * pctPerLevel).toFixed(1)}%)`}
          </button>
        </>
      )}
    </div>
  );
}


function SoulCrystalPanel({ scState, statsMeta, onRefresh }) {
  const [busyKey, setBusyKey] = useState(null);
  const [packs, setPacks] = useState(10);
  const [buying, setBuying] = useState(false);

  if (!scState) return null;
  const { inventory, sockets, pct_per_level, max_level, shop, wallets } = scState;

  const upgrade = async (statKey, levels) => {
    setBusyKey(statKey);
    try {
      const r = await axios.post(
        `${API}/hero/soul-crystals/upgrade`,
        { stat_key: statKey, levels_to_add: levels },
        { withCredentials: true }
      );
      toast.success(`💎 ${statKey.toUpperCase()} → Lv ${r.data.new_level} (+${(r.data.new_level * pct_per_level).toFixed(1)}%)`);
      onRefresh?.(r.data.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed");
    } finally {
      setBusyKey(null);
    }
  };

  const buy = async () => {
    if (packs <= 0) { toast.error("Enter a pack count"); return; }
    setBuying(true);
    try {
      const r = await axios.post(
        `${API}/hero/soul-crystals/buy`,
        { packs: Number(packs) },
        { withCredentials: true }
      );
      toast.success(`💎 +${r.data.crystals_gained} crystals for ${r.data.cost_pb} PB`);
      onRefresh?.(r.data.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="rounded-xl border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-950/30 to-black/40 p-4 mb-6" data-testid="soul-crystal-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Gem size={18} className="text-fuchsia-300" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-fuchsia-300">
              Soul Crystal Sanctum
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              +{pct_per_level}% per level · max Lv {max_level} (+{(pct_per_level * max_level).toFixed(0)}%)
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[11px] text-neutral-400" data-testid="soul-crystal-inventory">
            Inventory: <span className="font-mono font-bold text-fuchsia-300">{inventory.toLocaleString()}</span> 💎
          </div>
          <div className="text-[11px] text-neutral-400" data-testid="soul-crystal-pb-balance">
            PB: <span className="font-mono font-bold text-amber-300">{(wallets?.phoenix_blessings || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Shop bar */}
      <div className="rounded-lg border border-amber-500/30 bg-amber-950/10 p-2 mb-4 flex items-center gap-2 flex-wrap" data-testid="soul-crystal-shop">
        <ShoppingCart size={12} className="text-amber-300" />
        <span className="text-[10px] text-amber-200 font-bold uppercase tracking-widest">
          Guild Shop:
        </span>
        <span className="text-[10px] text-neutral-400">
          {shop?.pack_cost_pb} PB → {shop?.pack_size} crystals
        </span>
        <div className="flex items-center gap-1 ml-auto">
          <input
            type="number"
            min={1}
            max={shop?.max_pack_buy || 10000}
            value={packs}
            onChange={(e) => setPacks(Number(e.target.value))}
            data-testid="soul-crystal-buy-packs-input"
            className="w-20 bg-black/60 border border-neutral-700 text-[11px] font-mono text-white px-2 py-1 rounded"
          />
          <button
            onClick={buy}
            disabled={buying}
            data-testid="soul-crystal-buy-btn"
            className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold rounded border border-amber-500/60 bg-amber-900/30 text-amber-200 hover:bg-amber-900/60 disabled:opacity-40"
          >
            {buying ? "Buying..." : `Buy (${(packs * (shop?.pack_cost_pb || 1)).toLocaleString()} PB)`}
          </button>
        </div>
      </div>

      {/* Sockets grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="soul-crystal-sockets-grid">
        {sockets.map((s) => {
          const meta = statsMeta.find((m) => m.key === s.stat_key) || statsMeta[0];
          return (
            <SoulCrystalSocketRow
              key={s.stat_key}
              socket={s}
              meta={meta}
              inventory={inventory}
              pctPerLevel={pct_per_level}
              onUpgrade={upgrade}
              busyKey={busyKey}
            />
          );
        })}
      </div>

      <div className="text-[10px] text-neutral-500 italic mt-3 flex items-center gap-1">
        <Info size={11} />
        Crystals also drop from Lv 10+ mobs in the Wilds. Amplifier is applied at fight-time by the combat engine.
      </div>
    </div>
  );
}


// ─── 🛡️ Equipped Gear Panel ────────────────────────────────
const SLOT_LABELS = {
  weapon:    { label: "Weapon",    Icon: Sword },
  helmet:    { label: "Helmet",    Icon: Shield },
  helm:      { label: "Helm",      Icon: Shield },
  chest:     { label: "Chest",     Icon: Shield },
  wings:     { label: "Wings",     Icon: Wind },
  gloves:    { label: "Gloves",    Icon: Shield },
  talons:    { label: "Talons",    Icon: Zap },
  boots:     { label: "Boots",     Icon: Shield },
  legs:      { label: "Legs",      Icon: Shield },
  accessory: { label: "Accessory", Icon: Star },
  aura:      { label: "Aura",      Icon: SparkIcon },
};

function EquippedGearPanel({ equipment }) {
  if (!equipment) return null;
  const perSlot = equipment.per_slot || {};
  const sets    = equipment.sets     || [];
  const flat    = equipment.flat     || {};

  const populatedSlots = Object.entries(perSlot)
    .filter(([_slot, stats]) => stats && Object.keys(stats).length > 0);

  const totalGearHp   = Number(equipment.contrib?.hp   || 0);
  const totalGearPatk = Number(equipment.contrib?.patk || 0);
  const totalGearPdef = Number(equipment.contrib?.pdef || 0);

  return (
    <div
      className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-950/25 to-black/40 p-4 mb-6"
      data-testid="hero-equipped-gear-panel"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-amber-300" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-amber-300">
              Equipped Gear
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Passive stats layered into every card above.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap text-[11px]">
          {totalGearHp > 0 && (
            <span className="text-red-300">
              HP <span className="font-mono font-bold">+{Math.round(totalGearHp)}</span>
            </span>
          )}
          {totalGearPatk > 0 && (
            <span className="text-orange-300">
              PATK <span className="font-mono font-bold">+{Math.round(totalGearPatk)}</span>
            </span>
          )}
          {totalGearPdef > 0 && (
            <span className="text-blue-300">
              PDEF <span className="font-mono font-bold">+{Math.round(totalGearPdef)}</span>
            </span>
          )}
          {Object.keys(flat).length === 0 && (
            <span className="text-neutral-500 italic">No gear equipped</span>
          )}
        </div>
      </div>

      {populatedSlots.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-4" data-testid="hero-equipped-slots-grid">
          {populatedSlots.map(([slot, stats]) => {
            const cfg = SLOT_LABELS[slot] || { label: slot, Icon: Shield };
            const SIcon = cfg.Icon;
            const chips = Object.entries(stats).filter(([_k, v]) => typeof v === "number" && v !== 0);
            return (
              <div
                key={slot}
                data-testid={`hero-equipped-slot-${slot}`}
                className="rounded-lg border border-neutral-800 bg-black/40 p-2"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <SIcon size={11} className="text-amber-300" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-300">{cfg.label}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {chips.map(([k, v]) => (
                    <span key={k} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-950/40 border border-amber-500/20 text-amber-200">
                      +{v} {k.replace("_", "").toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sets.length > 0 && (
        <div className="border-t border-amber-500/20 pt-3" data-testid="hero-set-bonuses">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300 mb-2 flex items-center gap-1">
            <SparkIcon size={11} /> Active Set Bonuses
          </div>
          <div className="space-y-2">
            {sets.map((s) => (
              <div
                key={s.set_id}
                data-testid={`hero-set-${s.set_id}`}
                className="rounded-lg border border-amber-500/30 bg-amber-950/20 p-2"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-bold text-amber-200">{s.name}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-100">
                    {s.pieces_equipped} pcs equipped
                  </span>
                </div>
                <div className="space-y-1">
                  {(s.perks || []).map((p, i) => (
                    <div key={i} className="text-[10px] flex items-start gap-1.5">
                      <span className="font-mono text-amber-400 font-bold w-8">{p.pieces}pc</span>
                      <div className="flex-1">
                        <span className="text-amber-200 font-bold">{p.name}:</span>{" "}
                        <span className="text-neutral-400">{p.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── 🧪 Battle Bag Panel — healing consumables + timed effects ─
function fmtMMSS(sec) {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function BattleBagPanel({ bag, onRefresh }) {
  const [busyId, setBusyId] = useState(null);
  // 1-second ticker for the live countdown strip
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!bag?.active_effects?.length) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [bag?.active_effects?.length]);

  if (!bag) return null;
  const items  = bag.items || [];
  const active = bag.active_effects || [];
  const totals = bag.totals || {};

  const use = async (cid) => {
    setBusyId(cid);
    try {
      const r = await axios.post(
        `${API}/consumables/use`,
        { consumable_id: cid },
        { withCredentials: true },
      );
      onRefresh?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not use item");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/25 to-black/40 p-4 mb-6"
      data-testid="hero-battle-bag-panel"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-emerald-300" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-emerald-300">
              Battle Bag
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Healing consumables · used from combat (Arena / Battlegrounds).
            </div>
          </div>
        </div>
        <div className="text-[11px] text-neutral-400 flex items-center gap-3 flex-wrap">
          <span data-testid="battle-bag-total">
            Owned: <span className="font-mono font-bold text-emerald-300">{totals.total_owned || 0}</span>
          </span>
          <span data-testid="battle-bag-unique">
            Unique: <span className="font-mono font-bold text-emerald-300">{totals.unique_owned || 0}</span>
          </span>
          {(totals.active_count || 0) > 0 && (
            <span data-testid="battle-bag-active-count" className="text-yellow-300">
              Active: <span className="font-mono font-bold">{totals.active_count}</span>
            </span>
          )}
        </div>
      </div>

      {/* Active timed effects strip */}
      {active.length > 0 && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-950/20 p-2 mb-3" data-testid="battle-bag-active-strip">
          <div className="text-[10px] uppercase tracking-widest font-bold text-yellow-300 mb-1 flex items-center gap-1">
            <Clock size={11} /> Active Effects
          </div>
          <div className="flex flex-wrap gap-2">
            {active.map((eff) => (
              <div
                key={eff.effect_id}
                data-testid={`battle-bag-active-${eff.effect_id}`}
                className="flex items-center gap-1.5 px-2 py-1 rounded border border-yellow-500/40 bg-black/40"
              >
                <span className="text-lg">{eff.icon}</span>
                <div className="text-[10px]">
                  <div className="text-yellow-200 font-bold">{eff.name}</div>
                  <div className="text-neutral-400">
                    +{eff.heal_hp || eff.heal_mp || 0} every {eff.tick_sec}s
                  </div>
                </div>
                <span
                  data-testid={`battle-bag-timer-${eff.effect_id}`}
                  className="text-[11px] font-mono font-bold text-yellow-300 ml-1 tabular-nums"
                >
                  {fmtMMSS(eff.remaining_sec)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consumable grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="battle-bag-items-grid">
        {items.map((it) => {
          const owned  = it.count > 0;
          const isTimed = (it.duration_sec || 0) > 0;
          const busy   = busyId === it.consumable_id;
          return (
            <div
              key={it.consumable_id}
              data-testid={`battle-bag-item-${it.consumable_id}`}
              className={`relative rounded-lg border p-3 flex flex-col ${
                owned
                  ? "border-emerald-500/30 bg-black/40"
                  : "border-dashed border-neutral-800 bg-black/20 opacity-60"
              }`}
            >
              <div className="flex items-start justify-between mb-1">
                <span className="text-2xl">{it.icon}</span>
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                  style={{ backgroundColor: `${it.tier_meta?.color_hex || "#a3a3a3"}25`, color: it.tier_meta?.color_hex || "#a3a3a3" }}
                >
                  {it.tier_meta?.label || it.tier}
                </span>
              </div>
              <div className="font-bold text-white text-xs">{it.name}</div>
              <div className="text-[10px] text-neutral-400 mb-2 flex-1 leading-tight">
                {it.description}
              </div>
              <div className="flex items-center justify-between gap-2 mt-auto">
                <span
                  data-testid={`battle-bag-count-${it.consumable_id}`}
                  className={`text-sm font-mono font-bold ${owned ? "text-emerald-300" : "text-neutral-600"}`}
                >
                  ×{it.count}
                </span>
                {isTimed && owned ? (
                  <button
                    onClick={() => use(it.consumable_id)}
                    disabled={busy}
                    data-testid={`battle-bag-use-${it.consumable_id}`}
                    className="text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-emerald-500/60 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/50 disabled:opacity-40"
                  >
                    {busy ? "Using..." : "Activate"}
                  </button>
                ) : (
                  <span
                    className="text-[9px] uppercase tracking-widest text-neutral-500 border border-neutral-800 rounded px-1.5 py-0.5"
                    title="Fires from combat engine when your party targets an ally"
                  >
                    Battle only
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-neutral-500 italic mt-3 flex items-center gap-1">
        <Info size={11} />
        Instant heals fire from the combat engine when used in Arena or Battlegrounds. Timed regens (like Warm Broth) can be activated here — the countdown ticks in real time.
      </div>
    </div>
  );
}


// ─── Main Page ─────────────────────────────────────────────
export default function HeroStatSheetPage() {
  const { user } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLevel, setPreviewLevel] = useState(null);   // null = current
  const [refreshing, setRefreshing] = useState(false);
  const [scState, setScState] = useState(null);            // 💎 Soul Crystal snapshot
  const [bag, setBag] = useState(null);                    // 🧪 Battle Bag snapshot

  // Baseline (current-level) sheet — used to compute preview deltas
  const [baseline, setBaseline] = useState(null);

  const fetchSheet = async (lvl) => {
    const url = lvl != null ? `${API}/hero/stat-sheet?preview_level=${lvl}` : `${API}/hero/stat-sheet`;
    const r = await axios.get(url, { withCredentials: true });
    return r.data;
  };

  const fetchSoulCrystals = async () => {
    const r = await axios.get(`${API}/hero/soul-crystals/state`, { withCredentials: true });
    return r.data;
  };

  const fetchBag = async () => {
    const r = await axios.get(`${API}/consumables/bag`, { withCredentials: true });
    return r.data;
  };

  useEffect(() => {
    if (!user) return;
    Promise.all([
      fetchSheet(null),
      fetchSoulCrystals().catch(() => null),
      fetchBag().catch(() => null),
    ])
      .then(([data, sc, bg]) => {
        setSheet(data);
        setBaseline(data);
        setPreviewLevel(data?.hero?.current_level ?? data?.hero?.level ?? 1);
        if (sc) setScState(sc);
        if (bg) setBag(bg);
      })
      .catch(() => toast.error("Could not load stat sheet"))
      .finally(() => setLoading(false));
  }, [user]);

  const onSlideCommit = async (lvl) => {
    if (lvl === baseline?.hero?.current_level) {
      // Snap back to current-level fetch (already cached in baseline)
      setSheet(baseline);
      return;
    }
    setRefreshing(true);
    try {
      const data = await fetchSheet(lvl);
      setSheet(data);
    } catch {
      toast.error("Preview failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (!user)   return <div className="text-center py-16 text-neutral-500">Sign in to view your character sheet.</div>;
  if (loading) return <div className="text-center py-16 text-neutral-500">Loading stat sheet...</div>;
  if (!sheet)  return <div className="text-center py-16 text-neutral-500">No stat data available.</div>;

  const { hero, base, final, guild_buffs, equipment, apex_buffs } = sheet;
  const currentLvl = baseline?.hero?.current_level ?? baseline?.hero?.level ?? hero.level;
  const isPreview  = hero.is_preview;

  // Per-source contributions for the stat cards.
  const equipContrib = (key) => Number(equipment?.contrib?.[key] || 0);
  const apexContrib  = (key) => {
    const flat = apex_buffs?.flat || {};
    const pct  = apex_buffs?.pct  || {};
    if (key === "hp")   return Number(flat.hp   || 0);
    if (key === "patk") return Number(flat.patk || 0);
    if (key === "matk") return Number(flat.matk || 0);
    if (key === "pdef") return Number(flat.pdef || 0);
    if (key === "mdef") return Number(flat.mdef || 0);
    if (key === "crit") return Number(pct.crit_rate || 0);
    return 0;
  };
  // Guild buff = whatever's left after subtracting base + gear + apex from final.
  const getGuildBuff = (key) => {
    const b = base[key] || 0;
    const f = final[key] || 0;
    const rest = equipContrib(key) + apexContrib(key);
    const delta = f - b - rest;
    if (Math.abs(delta) < 0.01) return 0;
    return Number(delta.toFixed(2));
  };

  // For preview mode, compute delta vs baseline for each stat
  const getPreviewDelta = (key) => {
    if (!isPreview || !baseline) return 0;
    const cur = baseline.final?.[key] || 0;
    const now = final?.[key] || 0;
    const d = now - cur;
    if (Math.abs(d) < 0.001) return 0;
    return Number(d.toFixed(2));
  };

  return (
    <div className="max-w-6xl mx-auto p-4" data-testid="hero-stat-sheet-root">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-cyan-500/40 text-cyan-300 mb-3">
          <ScrollText size={14} />
          <span className="font-accent text-xs tracking-[0.3em] uppercase">Character Sheet</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-[0.9] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          {sheet.user.display_name}&apos;s <span className="text-cyan-300">Stat Sheet</span>
        </h2>
        <div className="flex items-center justify-center gap-3 mt-2">
          <span className="text-4xl">{hero.class_icon}</span>
          <div className="text-left">
            <div className="text-lg font-bold text-white">{hero.class_name}</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">
              Level <span className="text-emerald-300 font-mono">{hero.level}</span>
              {hero.rebirth_count > 0 && <> · Rebirth <span className="text-purple-300 font-mono">×{hero.rebirth_count}</span></>}
            </div>
          </div>
        </div>
      </div>

      {/* Guild-buff banner */}
      <div
        data-testid="hero-stat-guild-banner"
        className={`rounded-lg border p-3 mb-6 text-[11px] flex items-center gap-2 ${
          guild_buffs.in_guild
            ? "border-emerald-500/40 bg-emerald-950/20 text-emerald-300"
            : "border-neutral-800 bg-black/40 text-neutral-500"
        }`}
      >
        <Users size={14} />
        <span className="uppercase tracking-widest font-bold">
          {guild_buffs.in_guild
            ? "Guild Skill Shard buffs are active — reflected in the Final column below."
            : "Not in a guild — join one to layer Skill Shard buffs on your stats."}
        </span>
      </div>

      {/* Preview slider */}
      <div className="rounded-xl border border-cyan-500/30 bg-cyan-950/10 p-4 mb-6" data-testid="hero-stat-preview-slider">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-cyan-300" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-cyan-300">
              Level Preview
            </span>
          </div>
          <div className="text-[11px] text-neutral-400 flex items-center gap-2">
            {isPreview ? (
              <>
                <span className="text-yellow-300 font-bold">Projecting Lv {hero.level}</span>
                <span className="text-neutral-600">·</span>
                <span>Current Lv {currentLvl}</span>
                <button
                  onClick={() => { setPreviewLevel(currentLvl); setSheet(baseline); }}
                  data-testid="hero-stat-preview-reset"
                  className="ml-2 px-2 py-0.5 text-[9px] uppercase tracking-widest rounded border border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                >
                  Reset to Current
                </button>
              </>
            ) : (
              <span className="text-emerald-300">Showing Current Lv {currentLvl}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono w-8">Lv {currentLvl}</span>
          <input
            type="range"
            min={currentLvl}
            max={Math.min(currentLvl + 20, 150)}
            value={previewLevel ?? currentLvl}
            onChange={(e) => setPreviewLevel(Number(e.target.value))}
            onMouseUp={(e) => onSlideCommit(Number(e.target.value))}
            onTouchEnd={(e) => onSlideCommit(Number(e.target.value))}
            data-testid="hero-stat-preview-input"
            className="flex-1 accent-cyan-500"
          />
          <span className="text-lg font-bold font-mono text-cyan-300 w-14 text-right" data-testid="hero-stat-preview-level-display">
            Lv {previewLevel ?? currentLvl}
          </span>
        </div>
        {refreshing && (
          <div className="text-[10px] text-neutral-500 mt-1 italic text-right">Projecting...</div>
        )}
      </div>

      {/* Stat Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="hero-stat-grid">
        {STATS.map((meta) => (
          <StatCard
            key={meta.key}
            meta={meta}
            base={base[meta.key]}
            final={final[meta.key]}
            buff={getGuildBuff(meta.key)}
            equipContrib={equipContrib(meta.key)}
            apexContrib={apexContrib(meta.key)}
            previewDelta={getPreviewDelta(meta.key)}
            crystal={scState?.sockets?.find((s) => s.stat_key === meta.key)}
          />
        ))}
        <TBDCard label="Attack Speed" short="Atk Spd" Icon={Zap} />
        <TBDCard label="Dodge"        short="Dodge"   Icon={Wind} />
      </div>

      {/* 🛡️ Equipped Gear */}
      <EquippedGearPanel equipment={equipment} />

      {/* 🧪 Battle Bag */}
      <BattleBagPanel bag={bag} onRefresh={(next) => setBag(next)} />

      {/* 💎 Soul Crystal Sanctum */}
      <SoulCrystalPanel
        scState={scState}
        statsMeta={STATS}
        onRefresh={(next) => setScState(next)}
      />

      {/* Notes */}
      <div className="rounded-xl border border-neutral-800 bg-black/30 p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2 flex items-center gap-2">
          <Info size={12} /> Sheet Notes
        </div>
        <ul className="list-disc list-inside space-y-1 text-[11px] text-neutral-400 leading-relaxed">
          {(sheet.notes || []).map((n, i) => <li key={i}>{n}</li>)}
          <li>Equipment is layered into every stat above. Glimmer effects + Soul Crystal amplifiers still fire at fight-time.</li>
          <li>Attack Speed + Dodge become live stats once owner promotes them from TBD (see <span className="text-cyan-300">Stat Glossary canon</span>).</li>
        </ul>
      </div>
    </div>
  );
}
